import { FaClipboardList, FaEnvelope, FaFileAlt, FaSearchPlus, FaShoppingBag } from "react-icons/fa";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ProductNotFound from "../ProductNotFound";
import CTAExploreMore from "../CTAExploreMore";
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { GetShowcaseItems, GetShowcaseItemAttributes } from "../../../api/queries";
import { ShowcaseItem, ShowcaseItemAttribute } from "../../../api/zod-types";
// import ShowcaseItemAttributes from "./ShowcaseItemAttributes";
import { SafeLink } from "@/components/ui/SafeLink";
import Advertisement from "../ad/Advertisement";
import { GetAds } from "../../../api/queries";
import { Ad } from "../../../api/zod-types";
import { useAccumulate } from "@/components/context/AccumulateContext";



function slugify(str: string) {
    return str?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function ShowcaseItemDetail() {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // @ts-ignore
    const itemFromState = location.state?.product;
    // const { category, subCategory, slug } = useParams<{ category: string; subCategory: string; slug: string }>();
    const { category, subCategory, showcase, slug } = useParams<{ category: string; subCategory: string; showcase: string; slug: string }>();

    const navigate = useNavigate();

    // Hooks must be called at the top level, before any returns!
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const slideshowInterval = useRef<NodeJS.Timeout | null>(null);
    const [item, setItem] = useState<ShowcaseItem | null>(null);
    // local state
    // const [quoteCount, setQuoteCount] = useState(0);    
    // const [quoteItems, setQuoteItems] = useState<{ name: string; quantity: number }[]>([]);
    // global state from context
    const { quoteItems, setQuoteItems } = useAccumulate();

    // Fetch showcase items and find the item by slug
    const { data, isLoading } = useQuery({
        queryKey: ["showcaseitems"],
        staleTime: 60 * 1000 * 5,
        queryFn: () =>
            GetShowcaseItems().then(res => {
                let data = res.data;
                if (typeof data === "string") {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        console.error("Failed to parse showcase API data:", e, data);
                        return [];
                    }
                }
                return data.results || [];
            }),
    });

    // Fetch showcase item attributes
    const { data: attributesData, isLoading: attributesLoading } = useQuery({
        queryKey: ["showcaseitemattributes"],
        staleTime: 60 * 1000 * 5,
        queryFn: () =>
            GetShowcaseItemAttributes().then((res: any) => {
                let data = res.data;
                if (typeof data === "string") {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        console.error("Failed to parse showcase item attributes API data:", e, data);
                        return [];
                    }
                }
                return data.results || [];
            }),
    });

    // Fetch ads and pick one random ad
    const { data: adsData, isLoading: adsLoading } = useQuery({
        queryKey: ["ads"],
        queryFn: GetAds,
        staleTime: 0,
    });
    const ads: Ad[] = adsData && Array.isArray(adsData.data?.results) ? adsData.data.results : [];
    // const randomAd = ads.length > 0 ? ads[Math.floor(Math.random() * ads.length)] : null;
    const today = new Date();
    const validAds = ads.filter(ad => {
        const release = new Date(ad.adreleasedate);
        const expire = new Date(ad.adexpirationdate);
        return release <= today && expire >= today;
    });
    const randomAd = validAds.length > 0 ? validAds[Math.floor(Math.random() * validAds.length)] : null;
    useEffect(() => {
        if (itemFromState) {
            setItem(itemFromState);
            return;
        }
        if (!slug || !data) {
            setItem(null);
            return;
        }
        const found = data.find(
            (item: ShowcaseItem) =>
                slugify(item.productname) === slug ||
                slugify(item.showcaseitemname) === slug
        );
        setItem(found || null);
    }, [slug, data, itemFromState]);

    // Filter attribute items for this product
    const attributeItems: ShowcaseItemAttribute[] = Array.isArray(attributesData)
        ? attributesData.filter(attr => attr.showcaseitemname === item?.showcaseitemname)
        : [];
    // Prepare images for slideshow (attribute image or fallback to product image)
    // Sort so that isPrimary === true comes first
    const sortedAttributeItems = attributeItems.slice().sort((a, b) => {
        const aIsPrimary = a.productattributeisprimary === "true";
        const bIsPrimary = b.productattributeisprimary === "true";
        if (aIsPrimary === bIsPrimary) return 0;
        return aIsPrimary ? -1 : 1;
    });


    const slideshowImages =
        sortedAttributeItems.length > 0
            ? sortedAttributeItems
                .map(attr => attr.productattributeimageurl)
                .filter(url => typeof url === "string" && url.trim() !== "")
            : item?.productimage ? [item.productimage] : [];

    // Automatic slideshow effect
    useEffect(() => {
        if (slideshowImages.length > 1 && !lightboxOpen) {
            slideshowInterval.current = setInterval(() => {
                setActiveIndex(prev =>
                    prev === slideshowImages.length - 1 ? 0 : prev + 1
                );
            }, 3000); // Change slide every 3 seconds

            return () => {
                if (slideshowInterval.current) clearInterval(slideshowInterval.current);
            };
        }
        // Pause auto-slide when lightbox is open
        return () => {
            if (slideshowInterval.current) clearInterval(slideshowInterval.current);
        };
    }, [slideshowImages.length, lightboxOpen]);

    // Loading and not found checks (must be after hooks)
    if (isLoading || attributesLoading) {
        return <div className="text-center py-12 text-lg text-gray-500">Loading...</div>;
    }

    if (!item) {
        return (
            <ProductNotFound
                title="Oops! Item Not Found"
                message="The item you're looking for doesn't exist or may have been moved. Try browsing our catalogue."
                buttonText="Explore More Products"
                onGoBack={() => navigate(-1)}
            />
        );
    }

    const breadcrumb = (
        <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center text-sm text-gray-500 space-x-2">
                <li >
                    <SafeLink to="/" className="hover:underline  ">Home</SafeLink>
                </li>
                <li>/</li>
                <li>
                    <SafeLink to={`/products/${category}`} className="hover:underline">{category?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SafeLink>
                </li>
                <li>/</li>
                <li>
                    <SafeLink to={`/products/${category}/${subCategory}`} className="hover:underline">{subCategory?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SafeLink>
                </li>
                <li>/</li>
                <li>
                    <SafeLink to={`/products/${category}/${subCategory}/${showcase}`} className="hover:underline">{showcase?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SafeLink>
                </li>
                <li>/</li>
                <li className="text-black font-semibold">
                    {item?.productname || item?.showcaseitemname}
                </li>
            </ol>
        </nav>
    );


    const emailBody = encodeURIComponent(
        quoteItems.map(item => `- ${item.name} (Qty: ${item.quantity})`).join("\n")
    );

    return (
        // <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen px-2 lg:px-4">
        <div className="flex flex-col w-full bg-white pb-12 px-2 lg:px-20 xl:px-40">

            <div className="w-full max-w-4xl lg:max-w-7xl xl:max-w-full mx-auto mt-10 mb-8">


                {/* ads, items and breadcrumb */}
                <div className="flex flex-row gap-12">
                    {/* Left: Advertisement */}
                    <div className="hidden md:block">
                        {adsLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading advertisement...</div>
                        ) : (
                            <Advertisement ad={randomAd} />
                        )}
                    </div>

                    {/* Right: Breadcrumb and Item Details */}

                    <div >
                        {/* {breadcrumb} */}
                        <div className="flex items-center justify-between mb-6">
                            {breadcrumb}

                            {/* <a                                
                                href={`mailto:sam.sek@nz.harveynorman.com?subject=Quote Request&body=${encodeURIComponent(
                                    `I'd like to enquire the following items:\n\n` +
                                    quoteItems.map(item => `- ${item.name} (Qty: ${item.quantity})`).join("\n")
                                )}`}
                                className="relative ml-4"
                            >
                                
                                <FaEnvelope className="text-2xl text-harvey-red" />
                                {quoteCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-harvey-red text-white text-xs font-bold rounded-full px-2 py-0.5">
                                        {quoteCount}
                                    </span>
                                )}
                            </a> */}

                        </div>


                        {/* <div className="">
                        <Advertisement />
                    </div> */}


                        {/* Item detail content */}

                        <div className=" p-6 md:p-8 animate-fade-in-up">
                            <div className="flex flex-col md:flex-row md:items-stretch gap-8 lg:gap-8 xl:gap-10">
                                {/* Slideshow Section */}
                                <div className="flex flex-col items-center md:w-1/2 xl:w-7/12 px-0 py-0 lg:px-4 xl:px-6 lg:py-6 xl:py-6">
                                    <div className="relative group w-full lg:max-w-lg xl:max-w-xl aspect-square flex items-center animate-fade-in-up">
                                        <img
                                            src={slideshowImages[activeIndex]}
                                            alt={item.productname || item.showcaseitemname}
                                            className="w-full h-full rounded-2xl  transition-transform duration-300 group-hover:scale-105  cursor-zoom-in"
                                            loading="lazy"
                                            onClick={() => setLightboxOpen(true)}
                                            tabIndex={0}
                                            aria-label="Zoom image"
                                        />
                                        <button
                                            className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
                                            onClick={() => setLightboxOpen(true)}
                                            aria-label="Open image in lightbox"
                                        >
                                            <FaSearchPlus /> Zoom
                                        </button>
                                        {slideshowImages.length > 1 && (
                                            <>
                                                {/* <button
                                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setActiveIndex(i => (i === 0 ? slideshowImages.length - 1 : i - 1));
                                            }}
                                            aria-label="Previous image"
                                        >
                                            &#8592;
                                        </button>
                                        <button
                                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setActiveIndex(i => (i === slideshowImages.length - 1 ? 0 : i + 1));
                                            }}
                                            aria-label="Next image"
                                        >
                                            &#8594;
                                        </button> */}
                                            </>
                                        )}
                                    </div>
                                    {/* Thumbnails */}
                                    {slideshowImages.length > 1 && (
                                        <div className="flex gap-2 mt-4 justify-center">
                                            {slideshowImages.map((img, idx) => (
                                                <img
                                                    key={img + idx}
                                                    src={img}
                                                    alt={`Thumbnail ${idx + 1}`}
                                                    className={`w-16 h-16 object-cover rounded-lg border cursor-pointer transition-all ${activeIndex === idx ? "border-harvey-red ring-2 ring-harvey-red" : "border-gray-200"}`}
                                                    onClick={() => setActiveIndex(idx)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Title, description, and enquiry button */}
                                <div className="flex-1 flex flex-col justify-between px-0 py-0 lg:px-4 xl:px-6 lg:py-6 xl:py-6">
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                                            {item.productname || item.showcaseitemname}
                                        </h1>
                                        {item.producthtmldescription ? (
                                            <div
                                                className="prose prose-base max-w-none text-gray-800 
                                    [&_h2]:font-bold [&_h2]:text-lg [&_h2]:ml-0 [&_h3]:font-bold [&_h3]:text-base [&_p]:text-base
                                    [&_ul]:list-disc [&_ul]:ml-5  [&_li]:mb-1 [&_li]:pl-1
                                    [&_h2]:mb-2 [&_h3]:mb-2 [&_h3]:mt-4 [&_p]:mb-4"
                                                dangerouslySetInnerHTML={{ __html: item.producthtmldescription }}
                                            />
                                        ) : (
                                            <p className="prose prose-base max-w-none text-gray-800 
                                    [&_h2]:font-bold [&_h2]:text-lg [&_h2]:ml-0 [&_h3]:font-bold [&_h3]:text-base [&_p]:text-base
                                    [&_ul]:list-disc [&_ul]:ml-5  [&_li]:mb-1 [&_li]:pl-1
                                    [&_h2]:mb-2 [&_h3]:mb-2 [&_h3]:mt-4 [&_p]:mb-4" dangerouslySetInnerHTML={{ __html: item.showcaseitemdefaultdescription }} />
                                        )}
                                    </div>
                                    <div className="flex items-center justify-end mt-8 gap-2">
                                        <button
                                            className="bg-harvey-red text-white font-semibold px-8 lg:px-16 py-3 rounded-lg shadow hover:bg-harvey-red/90 transition flex items-center justify-center"
                                            // onClick={() => alert("Enquiry sent!")}
                                            onClick={() => {
                                                setQuoteItems(prev => [
                                                    ...prev,
                                                    { name: item.productname || item.showcaseitemname, quantity: 1 }
                                                ]);
                                            }}
                                        >
                                            <div className="flex items-center justify-center w-full ">
                                                {/* <p>Enquire Now</p> */}
                                                <p>Add to Enquiry List</p>
                                                <FaEnvelope className="ml-2" />
                                            </div>
                                        </button>
                                        {/* <button
                                    className="bg-harvey-red text-white font-semibold px-8 lg:px-16 py-3 rounded-lg shadow hover:bg-harvey-red/90 transition flex items-center justify-center"
                                    onClick={() => {
                                        setQuoteItems(prev => [
                                            ...prev,
                                            { name: item.productname || item.showcaseitemname, quantity: 1 }
                                        ]);
                                        setQuoteCount(quoteCount + 1);
                                    }}
                                >
                                    <div className="flex items-center justify-center w-full ">
                                        <span>Add to Quote</span>
                                        <FaShoppingBag className="ml-2" />
                                    </div>
                                </button> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <CTAExploreMore
                    text={(showcase || "Products").replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    suffix="Products"
                    to={`/products/${category}/${subCategory}/${showcase}`}
                />




                {/* Lightbox Modal */}
                {lightboxOpen && (
                    <div
                        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
                        onClick={() => setLightboxOpen(false)}
                        tabIndex={-1}
                        aria-modal="true"
                        role="dialog"
                    >
                        <img
                            src={slideshowImages[activeIndex]}
                            alt={item.productname || item.showcaseitemname}
                            className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl"
                            onClick={e => e.stopPropagation()}
                        />
                        <button
                            className="absolute top-4 right-4 text-white text-3xl font-bold"
                            onClick={() => setLightboxOpen(false)}
                            aria-label="Close lightbox"
                        >
                            &times;
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
}